import React from 'react';
import './SummaryTab.css';

/**
 * SummaryTab - Phase 0+1 content
 * Matches mockup: AI description + Quick qualification (chips, restrictions, totals, products table)
 *
 * Now uses the Qualification Agent results when available:
 * - qualification.can_bid: true/false based on tender type
 * - qualification.tender_type: "RFP", "RFI", etc.
 * - qualification.restrictions: [{type, details, severity}]
 * - qualification.products: [{name, quantity, wholesale_price, retail_price}]
 * - qualification.total_wholesale_cost: number
 */
const SummaryTab = ({
  tender,
  bidAssessment,
  phase1Data,
  phase2Data,
  phase2bData,
  phase2cData,
  qualification,
  qualificationLoading,
  loading,
  onRefresh
}) => {
  // Get bid type chip - use qualification result if available
  const getBidTypeChip = () => {
    // Use qualification agent result if available
    if (qualification) {
      const tenderType = qualification.tender_type || 'UNKNOWN';
      const canBid = qualification.can_bid;

      if (!canBid) {
        return { label: `Type: ${tenderType}`, className: 'bad' };
      }
      return { label: `Type: ${tenderType}`, className: 'good' };
    }

    // Fallback to manual detection
    const tenderType = (tender?.tender_type || tender?.notice_type || '').toLowerCase();
    const title = (tender?.title || '').toLowerCase();

    const eliminateTypes = ['rfi', 'noi', 'acan', 'rfso'];
    const proceedTypes = ['rfp', 'rfq', 'itq', 'tender', 'bid'];

    for (const type of eliminateTypes) {
      if (tenderType.includes(type) || title.includes(type)) {
        return { label: `Type: ${type.toUpperCase()}`, className: 'bad' };
      }
    }

    for (const type of proceedTypes) {
      if (tenderType.includes(type) || title.includes(type)) {
        return { label: `Type: ${type.toUpperCase()}`, className: 'good' };
      }
    }

    return { label: 'Type: Unknown', className: 'warn' };
  };

  // Get restrictions - use qualification result if available
  const getRestrictions = () => {
    // Use qualification agent result — supports both old 'restrictions' and new 'eliminations' format
    const items = qualification?.restrictions || qualification?.eliminations;
    if (items?.length > 0) {
      return items.map(r => {
        const typeLabel = r.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return r.details || r.description || typeLabel;
      });
    }

    // Fallback to manual detection from bid assessment and tender data
    const restrictions = [];
    const keyReqs = bidAssessment?.key_requirements || {};
    const bi = tender?.business_intelligence || {};

    // Security clearance
    const securityClearance = keyReqs.security_clearance || '';
    if (securityClearance && !securityClearance.toLowerCase().includes('none')) {
      restrictions.push('Security clearance required');
    }

    // RFSO membership
    if (tender?.title?.toLowerCase().includes('rfso') ||
        tender?.notice_type?.toLowerCase().includes('standing offer')) {
      restrictions.push('Only open to active RFSO members');
    }

    // Experience/references
    if (bi.experience_required || keyReqs.mandatory_qualifications?.length > 0) {
      restrictions.push('Customer references required');
    }

    // Certifications
    const certs = keyReqs.certifications_needed || [];
    if (certs.length > 0) {
      restrictions.push(`Certifications required: ${certs.join(', ')}`);
    }

    // Canadian content
    if (bi.canadian_made === 'yes' || bi['canadianMade-fabriquéCanadien'] === 'yes') {
      restrictions.push('Canadian content requirements apply');
    }

    // Indigenous business
    if (bi.indigenous_business_required || bi['indigenousBusinessRequired-entrepriseAutochtooneRequise']) {
      restrictions.push('Indigenous business participation required');
    }

    return restrictions;
  };

  // Products data - use qualification result, then phase1Data, then empty
  const rawProducts = qualification?.products || phase1Data?.products || [];
  const hasProducts = rawProducts.length > 0;

  // Build validated pricing lookup from Phase 2
  const validatedMap = {};
  if (phase2Data?.pricing_comparisons) {
    for (const pc of phase2Data.pricing_comparisons) {
      validatedMap[(pc.product_name || '').toLowerCase()] = pc;
    }
  }

  // Build Phase 2c strategy lookup
  const strategyMap = {};
  if (phase2cData?.product_strategies) {
    for (const ps of phase2cData.product_strategies) {
      strategyMap[(ps.product_name || '').toLowerCase()] = ps;
    }
  }

  // Build Google Shopping lookup from Phase 2b
  const shoppingListings = phase2bData?.shopping_listings || {};

  // Enrich products with phase2b web pricing when available
  const webPricingLookup = {};
  if (phase2bData?.product_pricing) {
    for (const wp of phase2bData.product_pricing) {
      const name = (wp.product_name || '').toLowerCase();
      if (name && wp.listed_price) {
        if (!webPricingLookup[name] || wp.listed_price < webPricingLookup[name].listed_price) {
          webPricingLookup[name] = wp;
        }
      }
    }
  }

  const products = rawProducts.map(p => {
    const name = (p.product_name || p.name || '').toLowerCase();
    const webPrice = webPricingLookup[name];
    const validated = validatedMap[name];
    const strategy = strategyMap[name];
    const shopItems = shoppingListings[p.product_name || p.name] || shoppingListings[name] || [];
    const bestShop = shopItems.find(s => s.price_cad) || null;

    return {
      ...p,
      web_price: bestShop?.price_cad || webPrice?.listed_price || null,
      web_source: bestShop?.merchant || webPrice?.source_name || null,
      web_url: webPrice?.source_url || null,
      best_cost: validated?.best_cost_cad || null,
      factory_landed: validated?.factory_landed_cad || strategy?.overseas?.landed_cost_cad || null,
      sourcing_route: validated?.sourcing_route || (strategy?.recommendation === 'OVERSEAS' ? 'factory' : strategy?.recommendation === 'DOMESTIC' ? 'domestic' : null),
      margin: validated?.margin_on_best_cost || null,
    };
  });

  // Calculate totals - use qualification results if available
  // Supports both old format (total_wholesale_cost) and new format (total_wholesale_estimate)
  const domesticTotal = qualification?.total_wholesale_cost || qualification?.total_wholesale_estimate ||
    products.reduce((sum, p) => {
      const qty = parseFloat(p.quantity) || 0;
      const unitPrice = parseFloat(p.wholesale_price || p.wholesale_price_cad || p.domestic_unit_price || p.domesticUnitPrice) || 0;
      return sum + (qty * unitPrice);
    }, 0);

  const oemTotal = qualification?.total_retail_cost || qualification?.total_retail_estimate ||
    products.reduce((sum, p) => {
      const qty = parseFloat(p.quantity) || 0;
      const unitPrice = parseFloat(p.retail_price || p.retail_price_cad || p.oem_unit_price || p.oemUnitPrice) || 0;
      return sum + (qty * unitPrice);
    }, 0);

  const formatCurrency = (value) => {
    if (!value || value === 0) return '—';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const bidTypeChip = getBidTypeChip();
  const restrictions = getRestrictions();
  const hasRestrictions = restrictions.length > 0;

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="spinner"></div>
        <p>Analyzing tender documents...</p>
      </div>
    );
  }

  return (
    <div className="summary-tab-v2">
      {/* AI Description Card */}
      <div className="card">
        <h3>AI Description</h3>
        <div className="ai-desc">
          {(qualification?.ai_summary || qualification?.ai_description) ? (
            <p dangerouslySetInnerHTML={{
              __html: (qualification.ai_summary || qualification.ai_description)
                .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
                .replace(/\n/g, '<br />')
            }} />
          ) : qualificationLoading ? (
            <p className="muted">
              <span className="spinner-inline"></span> Analyzing tender...
            </p>
          ) : (tender?.['aiDescription-descriptionIA-eng'] || tender?.description) ? (
            <p dangerouslySetInnerHTML={{
              __html: (tender['aiDescription-descriptionIA-eng'] || tender.description)
                .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
                .replace(/\n/g, '<br />')
            }} />
          ) : (
            <p className="muted">
              No description available for this tender.
            </p>
          )}
        </div>
      </div>

      {/* Quick Qualification Card */}
      <div className="card" style={{ marginTop: '14px' }}>
        <h3>Quick qualification</h3>

        {/* Chips row */}
        <div className="pillline">
          <span className={`chip ${bidTypeChip.className}`}>
            <span className="dot"></span>
            {bidTypeChip.label}
          </span>
          {hasRestrictions && (
            <span className="chip bad">
              <span className="dot"></span>
              Restrictions present
            </span>
          )}
          {!hasRestrictions && (
            <span className="chip good">
              <span className="dot"></span>
              No restrictions detected
            </span>
          )}
        </div>

        {/* Restrictions bullet list */}
        {hasRestrictions && (
          <div className="sub" style={{ marginTop: '6px' }}>
            • {restrictions.join(' • ')}
          </div>
        )}

        {/* Tender Requirements — sourced from Phase 1 AI qualification */}
        {qualification?.tender_requirements && (() => {
          const tr = qualification.tender_requirements;
          return (
            <div style={{ marginTop: '12px' }}>
              <div className="sub" style={{ fontWeight: 600, marginBottom: '6px' }}>Tender Requirements</div>
              <div className="pillline">
                <span className={`chip ${
                  tr.canadian_content === 'yes' ? 'bad' :
                  tr.canadian_content === 'no' ? 'good' : 'neutral'
                }`}>
                  <span className="dot"></span>
                  Canadian Content: {tr.canadian_content === 'not_specified' ? 'N/A' : tr.canadian_content}
                </span>
                <span className={`chip ${tr.experience_required ? 'warn' : 'good'}`}>
                  <span className="dot"></span>
                  Experience Required: {tr.experience_required ? 'Yes' : 'No'}
                </span>
                <span className={`chip ${tr.indigenous_business ? 'warn' : 'good'}`}>
                  <span className="dot"></span>
                  Indigenous Business: {tr.indigenous_business ? 'Required' : 'Not Required'}
                </span>
                {tr.security_clearance && (
                  <span className="chip bad">
                    <span className="dot"></span>
                    Security: {tr.security_clearance}
                  </span>
                )}
                {tr.category && (
                  <span className="chip neutral">
                    <span className="dot"></span>
                    Category: {tr.category}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Totals boxes */}
        <div className="totals">
          <div className="total-box">
            <div className="k">Total estimated cost (domestic wholesale)</div>
            <div className="v">
              {hasProducts ? formatCurrency(domesticTotal) : 'Not yet generated'}
            </div>
          </div>
          <div className="total-box">
            <div className="k">Total estimated cost (retail)</div>
            <div className="v">
              {hasProducts ? formatCurrency(oemTotal) : 'Not yet generated'}
            </div>
          </div>
          {products.some(p => p.best_cost) && (
            <div className="total-box" style={{ borderColor: '#16a34a' }}>
              <div className="k">Best validated cost</div>
              <div className="v" style={{ color: '#16a34a' }}>
                {formatCurrency(products.reduce((sum, p) => {
                  const qty = parseFloat(p.quantity) || 0;
                  const cost = parseFloat(p.best_cost) || parseFloat(p.wholesale_price || p.wholesale_price_cad) || 0;
                  return sum + (qty * cost);
                }, 0))}
              </div>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div style={{ overflowX: 'auto' }}>
          {hasProducts ? (
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: '220px' }}>Product</th>
                  <th className="num">Qty</th>
                  <th className="num">Domestic / unit</th>
                  <th className="num">Retail / unit</th>
                  <th className="num">Market Price</th>
                  {products.some(p => p.best_cost) && (
                    <th className="num">Best Cost</th>
                  )}
                  {products.some(p => p.sourcing_route) && (
                    <th>Route</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => {
                  const qty = parseFloat(product.quantity) || 0;
                  // Handle qualification format (wholesale_price_cad), old format, and legacy format
                  const domesticUnit = parseFloat(
                    product.wholesale_price || product.wholesale_price_cad || product.domestic_unit_price || product.domesticUnitPrice
                  ) || 0;
                  const oemUnit = parseFloat(
                    product.retail_price || product.retail_price_cad || product.oem_unit_price || product.oemUnitPrice
                  ) || 0;

                  // Check if this is a generic category with no real pricing
                  const isGenericCategory = !domesticUnit && !oemUnit &&
                    (product.quantity === 'Variable - as and when requested' || product.quantity === 'Variable');

                  return (
                    <tr key={idx} className={isGenericCategory ? 'row-generic' : ''}>
                      <td>
                        {product.name || product.product_name}
                        {product.unit && <div className="sub">{product.unit}</div>}
                        {isGenericCategory && (
                          <div className="sub" style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            Category — pricing requires specific SKUs
                          </div>
                        )}
                      </td>
                      <td className="num">{isGenericCategory ? 'Variable' : qty.toLocaleString()}</td>
                      <td className="num">{domesticUnit ? formatCurrency(domesticUnit) : '—'}</td>
                      <td className="num">{oemUnit ? formatCurrency(oemUnit) : '—'}</td>
                      <td className="num">
                        {product.web_price ? (
                          <>
                            {formatCurrency(product.web_price)}
                            {product.web_source && (
                              <div className="sub">{product.web_source}</div>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      {products.some(p => p.best_cost) && (
                        <td className="num" style={product.best_cost ? { fontWeight: 600, color: '#16a34a' } : undefined}>
                          {product.best_cost ? formatCurrency(product.best_cost) : '—'}
                          {product.factory_landed && product.sourcing_route === 'factory' && (
                            <div className="sub" style={{ color: '#be185d' }}>Factory: {formatCurrency(product.factory_landed)}</div>
                          )}
                        </td>
                      )}
                      {products.some(p => p.sourcing_route) && (
                        <td>
                          {product.sourcing_route && (
                            <span style={{
                              fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600,
                              background: product.sourcing_route === 'factory' ? '#fce7f3' : product.sourcing_route === 'domestic' ? '#dbeafe' : '#f3f4f6',
                              color: product.sourcing_route === 'factory' ? '#be185d' : product.sourcing_route === 'domestic' ? '#1d4ed8' : '#6b7280',
                            }}>
                              {product.sourcing_route === 'factory' ? 'Factory' : product.sourcing_route === 'domestic' ? 'Domestic' : product.sourcing_route}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="placeholder-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: '220px' }}>Product</th>
                    <th className="num">Qty</th>
                    <th className="num">Domestic / unit</th>
                    <th className="num">Retail / unit</th>
                    <th className="num">Market Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <div className="empty-content">
                        <span className="empty-icon">📦</span>
                        <span className="empty-text">Extracting products...</span>
                        <span className="empty-hint">Products and pricing are being extracted from tender documents</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="sub" style={{ marginTop: '10px' }}>
          Notes: Estimates exclude freight, duties, taxes, inspection, and overhead.
          {!hasProducts && ' Product extraction in progress...'}
        </div>
      </div>
    </div>
  );
};

export default SummaryTab;
